"use client";

import { setSectorGuideClosed, setSectorGuideOpen } from "@utils/globalClassHelper";
import { getPersonalInfoByAudienceId } from "@utils/targetGroup/getPersonalInfo";
import { getRoleById, getSectorById } from "@utils/targetGroup/targetGroupHelper";
import { ChangeEvent, useEffect, useRef, useState } from "react";

import useParamsToggler from "hooks/useParamToggler";
import { ISectorToggler, useSectorToggler } from "hooks/zustand/useSectorToggle";
import { IRole, ISector } from "types/PersonalInfo";

import SectorPicker from "./SectorPicker/SectorPicker";
import { sectorTracker, sectorViewTracker } from "./sectorTracker";

const SectorGuide = () => {
  const sectorToggleRef = useRef(null);
  const { isOpen, removeQueryParameter, appendQueryParameter, closeModal } = useParamsToggler();
  const [sector, setSector] = useState<ISector>();
  const [role, setRole] = useState<IRole>();
  const [openGuide, setOpenGuide] = useState<boolean>();
  const setSelectedSector = useSectorToggler((state: ISectorToggler) => state.setSelectedSector);
  const selectedSector = useSectorToggler((state: ISectorToggler) => state.selectedSector);

  useEffect(() => {
    if (isOpen) {
      setSectorGuideOpen();
    } else {
      setSectorGuideClosed();
    }
  }, [isOpen]);

  useEffect(() => {
    const currentSector = getSectorById(selectedSector?.sector?.sectorId);
    const currentRole = getRoleById(selectedSector?.role?.roleId);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSector(currentSector);
    setRole(currentRole);

    const openSectorGuide = selectedSector?.sector?.sectorId ? true : false;
    setOpenGuide(openSectorGuide);
  }, [selectedSector]);

  const handleSectorChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const sectorId = event?.target?.value;
    const selectedSector = getSectorById(sectorId);
    setSector(selectedSector);
    setOpenGuide(true);
    setTimeout(() => {
      const element = document.querySelector("#scrollForSubmit");
      element.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  const openClose = (closedBySubmit: boolean) => {
    if (isOpen && !closedBySubmit) {
      const sector = getSectorById(selectedSector?.sector?.sectorId);
      const role = getRoleById(selectedSector?.role?.roleId);
      sectorTracker(sector?.defaultName, role?.defaultName, "målgruppsväljare_pop_up", "avbryt");
    } else if (!isOpen) {
      sectorViewTracker("målgruppsväljare_pop_up");
    }
    if (isOpen) {
      removeQueryParameter();
    } else {
      appendQueryParameter("openSector", "true");
    }
    isOpen && sectorToggleRef?.current?.focus();
  };

  const handleRoleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const roleId = event?.target?.value;
    const selectedRole = getRoleById(roleId);
    setRole(selectedRole);
  };

  const handleSubmit = async () => {
    if (!role || !sector) {
      return;
    }

    const audienceId = role.roleId + "-" + sector.sectorId;
    await fetch("/api/personalization/personalization", {
      method: "POST",
      headers: { contentType: "application/json" },
      body: JSON.stringify({ audienceId }),
    });
    //Målgruppsväljaren har bytt målgrupp. Skicka ett event om detta.
    const targetGroupChangedEvent = new CustomEvent("targetGroupChanged", {
      detail: audienceId,
    } as CustomEventInit);
    document.dispatchEvent(targetGroupChangedEvent);

    setSelectedSector(getPersonalInfoByAudienceId(audienceId));
    openClose(true);
    sectorTracker(sector?.defaultName, role?.defaultName, "målgruppsväljare_pop_up", "bekräfta");
    window.scrollTo({
      top: 0,
      behavior: "auto",
    });
    sectorToggleRef?.current?.focus();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <SectorPicker
      submit={() => handleSubmit()}
      onSelectChange={handleSectorChange}
      onRoleChange={(event) => handleRoleChange(event)}
      selectedRole={role}
      formValid={openGuide}
      selectedSector={sector}
      openClose={(closedBySubmit) => {
        openClose(closedBySubmit);
        closeModal();
      }}
      sectorToggleRef={sectorToggleRef}
    />
  );
};

export default SectorGuide;
